'use client'

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { SessionCache } from '@/lib/session-cache'
import type { UserRole } from '@planner/types'

interface AuthContextType {
  user: User | null
  userRole: UserRole | null
  isSuperadmin: boolean
  isImpersonating: boolean
  impersonatedFromEmail: string | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: any }>
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: any }>
  signOut: () => Promise<void>
  updateUserRole: (role: UserRole) => Promise<void>
  startImpersonation: (actionLink: string) => Promise<{ error: string | null }>
  stopImpersonation: () => Promise<{ error: string | null }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const SESSION_CACHE_KEY = 'supabase.auth.session'
const USER_ROLE_CACHE_KEY = 'supabase.auth.userRole'
const USER_SUPERADMIN_CACHE_KEY = 'supabase.auth.isSuperadmin'
const SUPERADMIN_SESSION_CACHE_KEY = 'supabase.auth.superadminSession'
const IMPERSONATION_ACTIVE_KEY = 'supabase.auth.impersonationActive'
const IMPERSONATION_FROM_EMAIL_KEY = 'supabase.auth.impersonationFromEmail'

// Load initial state from cache synchronously
function loadInitialStateFromCache(): {
  user: User | null
  userRole: UserRole | null
  isSuperadmin: boolean
  session: Session | null
  hasCache: boolean
  isImpersonating: boolean
  impersonatedFromEmail: string | null
} {
  if (typeof window === 'undefined') {
    return {
      user: null,
      userRole: null,
      isSuperadmin: false,
      session: null,
      hasCache: false,
      isImpersonating: false,
      impersonatedFromEmail: null,
    }
  }

  let cachedSuperadmin = 'false'
  let cachedImpersonation = 'false'
  let cachedImpersonationFrom: string | null = null

  try {
    const cachedSession = localStorage.getItem(SESSION_CACHE_KEY)
    const cachedRole = localStorage.getItem(USER_ROLE_CACHE_KEY)
    cachedSuperadmin = localStorage.getItem(USER_SUPERADMIN_CACHE_KEY) || 'false'
    cachedImpersonation = localStorage.getItem(IMPERSONATION_ACTIVE_KEY) || 'false'
    cachedImpersonationFrom = localStorage.getItem(IMPERSONATION_FROM_EMAIL_KEY)

    if (cachedSession) {
      try {
        const parsed = JSON.parse(cachedSession)
        if (parsed?.user) {
          return {
            user: parsed.user,
            userRole: (cachedRole as UserRole) || null,
            isSuperadmin: cachedSuperadmin === 'true',
            session: parsed,
            hasCache: true,
            isImpersonating: cachedImpersonation === 'true',
            impersonatedFromEmail: cachedImpersonationFrom || null,
          }
        }
      } catch (e) {
        // Invalid cache, ignore
      }
    }
  } catch (e) {
    // Ignore cache errors
  }

  return {
    user: null,
    userRole: null,
    isSuperadmin: cachedSuperadmin === 'true',
    session: null,
    hasCache: false,
    isImpersonating: cachedImpersonation === 'true',
    impersonatedFromEmail: cachedImpersonationFrom || null,
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Initialize state from cache synchronously
  const initialState = loadInitialStateFromCache()
  const [user, setUser] = useState<User | null>(initialState.user)
  const [userRole, setUserRole] = useState<UserRole | null>(initialState.userRole)
  const [isSuperadmin, setIsSuperadmin] = useState<boolean>(initialState.isSuperadmin)
  const [isImpersonating, setIsImpersonating] = useState<boolean>(initialState.isImpersonating)
  const [impersonatedFromEmail, setImpersonatedFromEmail] = useState<string | null>(
    initialState.impersonatedFromEmail
  )
  const [session, setSession] = useState<Session | null>(initialState.session)
  // loading is false if we have cache, true if we don't
  const [loading, setLoading] = useState(!initialState.hasCache)
  const mountedRef = useRef(false)
  // AbortController for getSession - to cancel hanging requests
  const getSessionAbortControllerRef = useRef<AbortController | null>(null)
  const fetchRoleAbortControllerRef = useRef<AbortController | null>(null)
  // Ref to track current userRole for use in callbacks
  const userRoleRef = useRef<UserRole | null>(initialState.userRole)

  const fetchUserRole = useCallback(async (userId: string, signal?: AbortSignal) => {
    const fetchRoleStartTime = Date.now()
    
    try {
      // Create timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('fetchUserRole timeout'))
        }, 10000) // 10 second timeout

        // Clear timeout if aborted
        if (signal) {
          signal.addEventListener('abort', () => {
            clearTimeout(timeout)
            reject(new Error('fetchUserRole aborted'))
          })
        }
      })

      // Race between fetch and timeout
      const fetchPromise = supabase
        .from('users')
        .select('role, is_superadmin')
        .eq('id', userId)
        .single()

      const { data, error } = await Promise.race([fetchPromise, timeoutPromise])

      if (signal?.aborted) {
        return // Request was aborted
      }

      if (error) throw error
      const role = (data as { role: UserRole } | null)?.role || null
      const superadmin = (data as { is_superadmin?: boolean } | null)?.is_superadmin || false
      setUserRole(role)
      userRoleRef.current = role
      setIsSuperadmin(superadmin)

      // Cache the role using SessionCache
      SessionCache.setSession(session, role, superadmin)
      try {
        localStorage.setItem(USER_SUPERADMIN_CACHE_KEY, superadmin ? 'true' : 'false')
      } catch (e) {
        // Ignore cache errors
      }
      const fetchRoleDuration = Date.now() - fetchRoleStartTime
      void fetchRoleDuration
      console.log('[AuthContext] fetchUserRole completed, role:', role)
    } catch (error: any) {
      // Don't log if aborted or timeout - it's expected
      if (error?.message !== 'fetchUserRole aborted' && error?.message !== 'fetchUserRole timeout') {
        console.error('[AuthContext] Error fetching user role:', error)
      }
      // Don't block UI on error - keep current role
    }
  }, [])

  const updateSessionCache = useCallback(
    (session: Session | null, role?: UserRole | null, superadminFlag?: boolean) => {
    try {
      // Use SessionCache singleton for aggressive caching
      const cachedSuperadmin = typeof superadminFlag === 'boolean' ? superadminFlag : isSuperadmin
      SessionCache.setSession(session, role || userRole, cachedSuperadmin)
      console.log('[AuthContext] updateSessionCache called, session:', !!session, 'role:', role || userRole)
    } catch (e) {
      console.error('[AuthContext] Cache error:', e)
    }
  }, [userRole, isSuperadmin])

  // Always check cache on mount - even if component was remounted after tab switch
  // This ensures state is restored immediately, preventing "Ładowanie..." flash
  useEffect(() => {
    const timestamp = new Date().toISOString()
    console.log(`[AuthContext] useEffect mount check at ${timestamp}`)
    
    if (typeof window !== 'undefined') {
      try {
        const cachedSession = localStorage.getItem(SESSION_CACHE_KEY)
        const cachedRole = localStorage.getItem(USER_ROLE_CACHE_KEY)
        const cachedSuperadmin = localStorage.getItem(USER_SUPERADMIN_CACHE_KEY)
        console.log(`[AuthContext] Mount check - hasSessionCache=${!!cachedSession}, hasRoleCache=${!!cachedRole}`)
        
        if (cachedSession) {
          const parsed = JSON.parse(cachedSession)
          if (parsed?.user) {
            console.log(`[AuthContext] Mount check - restoring from cache: user=${parsed.user.email}, loading=${loading}`)
            // Restore from cache immediately - even if component was remounted
            setUser(parsed.user)
            setSession(parsed)
            setLoading(false)
            
            // Restore role from cache if available
            if (cachedRole) {
              console.log(`[AuthContext] Mount check - restoring role: ${cachedRole}`)
              setUserRole(cachedRole as UserRole)
              userRoleRef.current = cachedRole as UserRole
            }
            if (cachedSuperadmin !== null) {
              setIsSuperadmin(cachedSuperadmin === 'true')
            }
          } else {
            console.log('[AuthContext] Mount check - cache exists but no user in parsed session')
          }
        } else {
          console.log('[AuthContext] Mount check - no cache found')
        }
      } catch (e) {
        console.error('[AuthContext] Mount check - error:', e)
      }
    }
  }, []) // Run on every mount - ensures cache is restored even after remount

  useEffect(() => {
    const mountStartTime = Date.now()
    void mountStartTime
    
    // Prevent double mounting in React 18 strict mode
    if (mountedRef.current) {
      return
    }
    mountedRef.current = true

    // State is already initialized from cache
    // Set loading to false immediately - cache is already loaded
    setLoading(false)

    // If we have cache, DON'T call getSession() - it can hang and cause delays
    // onAuthStateChange will handle session updates automatically
    // Only call getSession() if we DON'T have cache (first time login)
    const hasCache = initialState.hasCache

    void hasCache

    // Check if we're in demo mode
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
    const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
    const isDemoMode = process.env.NODE_ENV === 'development' && 
                      (!supabaseUrl || !supabaseAnonKey || supabaseUrl.includes('placeholder'))

    if (!hasCache) {
      // In demo mode, don't call getSession - it would fail
      if (isDemoMode) {
        setLoading(false)
        return
      }

      // No cache - verify session with Supabase (first time or after logout)
      const getSessionStartTime = Date.now()
      void getSessionStartTime
      
      const abortController = new AbortController()
      getSessionAbortControllerRef.current = abortController

      // Create timeout promise (5 seconds)
      const timeoutPromise = new Promise<never>((_, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('getSession timeout'))
        }, 5000) // 5 second timeout

        // Clear timeout if aborted
        abortController.signal.addEventListener('abort', () => {
          clearTimeout(timeout)
          reject(new Error('getSession aborted'))
        })
      })

      // Verify session with Supabase in background (non-blocking)
      const getSessionPromise = supabase.auth.getSession()

      Promise.race([getSessionPromise, timeoutPromise])
        .then((result: any) => {
          if (abortController.signal.aborted) {
            return // Request was aborted
          }

          const { data: { session }, error } = result

          if (error) {
            // Log error but don't clear cache - token might refresh
            console.error('[AuthContext] getSession error:', error)
            setLoading(false) // Always set loading to false
            return
          }

          if (session) {
            // Valid session - update cache and state
            updateSessionCache(session)
            setSession(session)
            setUser(session.user ?? null)
            if (session.user) {
              // Fetch role in background with abort signal
              const roleAbortController = new AbortController()
              fetchRoleAbortControllerRef.current = roleAbortController
              fetchUserRole(session.user.id, roleAbortController.signal).catch(() => {
                // Silently fail
              })
            }
          }
          // If no session, don't clear cache - might be network issue
          // onAuthStateChange will handle it
          setLoading(false) // Always set loading to false
        })
        .catch((error: any) => {
          if (abortController.signal.aborted) {
            return // Request was aborted, ignore
          }

          // Timeout or network error - don't clear cache
          if (error?.message === 'getSession timeout') {
            console.warn('[AuthContext] getSession timeout - using cache')
          } else {
            console.error('[AuthContext] getSession exception:', error)
          }
          // Always set loading to false - use cache
          setLoading(false)
        })
    }
    // If we have cache, skip getSession() - onAuthStateChange will handle updates
    // Note: supabaseUrl, supabaseAnonKey, and isDemoMode are already declared above (line 221)

    // Listen for auth changes - this is the authoritative source
    // In demo mode, skip onAuthStateChange to prevent clearing demo session
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // In demo mode, ignore auth state changes (they would clear our mock session)
      if (isDemoMode) {
        // Check if we have a demo session in cache - if so, keep it
        const hasDemoCache = typeof window !== 'undefined' && (() => {
          try {
            const cachedSession = localStorage.getItem(SESSION_CACHE_KEY)
            if (cachedSession) {
              const parsed = JSON.parse(cachedSession)
              // If it's a demo session (starts with 'demo-'), keep it
              if (parsed?.user?.id?.startsWith('demo-')) {
                return true
              }
            }
          } catch (e) {
            // Invalid cache
          }
          return false
        })()
        
        if (hasDemoCache) {
          // Keep demo session, ignore Supabase auth changes
          console.log('[AuthContext] Demo mode: Ignoring onAuthStateChange to preserve demo session')
          return
        }
      }

      // Check if we have cache - if so, loading should always be false
      const hasCache = typeof window !== 'undefined' && (() => {
        try {
          const cachedSession = localStorage.getItem(SESSION_CACHE_KEY)
          if (cachedSession) {
            const parsed = JSON.parse(cachedSession)
            return !!parsed?.user
          }
        } catch (e) {
          // Invalid cache
        }
        return false
      })()

      // Handle SIGNED_OUT - clear everything
      if (event === 'SIGNED_OUT') {
        updateSessionCache(null)
        setSession(null)
        setUser(null)
        setUserRole(null)
        setIsSuperadmin(false)
        setIsImpersonating(false)
        setImpersonatedFromEmail(null)
        if (typeof window !== 'undefined') {
          localStorage.removeItem(SUPERADMIN_SESSION_CACHE_KEY)
          localStorage.removeItem(IMPERSONATION_ACTIVE_KEY)
          localStorage.removeItem(IMPERSONATION_FROM_EMAIL_KEY)
        }
        userRoleRef.current = null
        setLoading(false)
        return
      }

      // Handle TOKEN_REFRESHED - update session if we have one
      if (event === 'TOKEN_REFRESHED') {
        if (session) {
          updateSessionCache(session)
          setSession(session)
          setUser(session.user ?? null)
          if (session.user) {
            // Create abort controller for role fetch
            const roleAbortController = new AbortController()
            fetchRoleAbortControllerRef.current = roleAbortController
            await fetchUserRole(session.user.id, roleAbortController.signal)
          }
        }
        // Always set loading to false - never true if we have cache
        setLoading(false)
        return
      }

      // For all other events (SIGNED_IN, USER_UPDATED, etc.)
      if (session) {
        updateSessionCache(session)
        setSession(session)
        setUser(session.user ?? null)
        if (session.user) {
          // Check if role is already in cache - if so, skip fetchUserRole
          // This prevents unnecessary fetch after tab switch
            const cachedRole = typeof window !== 'undefined' ? localStorage.getItem(USER_ROLE_CACHE_KEY) : null
            const cachedSuperadmin =
              typeof window !== 'undefined' ? localStorage.getItem(USER_SUPERADMIN_CACHE_KEY) : null
          const currentUserRole = userRoleRef.current
          if (cachedRole && currentUserRole === cachedRole) {
            // Role is already loaded, no need to fetch
            // But make sure state is set
            if (!currentUserRole) {
              setUserRole(cachedRole as UserRole)
              userRoleRef.current = cachedRole as UserRole
            }
              if (cachedSuperadmin !== null) {
                setIsSuperadmin(cachedSuperadmin === 'true')
              }
          } else if (!currentUserRole) {
            // Only fetch if we don't have role yet
            // Create abort controller for role fetch
            const roleAbortController = new AbortController()
            fetchRoleAbortControllerRef.current = roleAbortController
            await fetchUserRole(session.user.id, roleAbortController.signal)
          }
        } else {
          setUserRole(null)
          setIsSuperadmin(false)
          userRoleRef.current = null
        }
      } else {
        // Session is null but event is not SIGNED_OUT - don't clear state
        // This can happen during token refresh or network issues
        // Keep existing state and cache
      }

      // Always set loading to false - NEVER set it to true
      // If we have cache, loading must be false
      setLoading(false)
    })

  // Handle visibility change - abort hanging requests when tab loses focus
  const handleVisibilityChange = () => {
    if (typeof window === 'undefined') return
    const timestamp = new Date().toISOString()
    const isHidden = document.hidden
    
    console.log(`[AuthContext] visibilitychange: hidden=${isHidden} at ${timestamp}`)
    
    if (document.hidden) {
      // Tab lost focus - abort hanging requests
      console.log('[AuthContext] Tab lost focus - aborting requests')
      if (getSessionAbortControllerRef.current) {
        getSessionAbortControllerRef.current.abort()
        getSessionAbortControllerRef.current = null
        console.log('[AuthContext] Aborted getSession request')
      }
      if (fetchRoleAbortControllerRef.current) {
        fetchRoleAbortControllerRef.current.abort()
        fetchRoleAbortControllerRef.current = null
        console.log('[AuthContext] Aborted fetchRole request')
      }

      // Ensure loading is false if we have cache
      if (typeof window !== 'undefined') {
        try {
          const cachedSession = localStorage.getItem(SESSION_CACHE_KEY)
          console.log(`[AuthContext] Tab hidden - cache check: hasCache=${!!cachedSession}`)
          
          if (cachedSession) {
            const parsed = JSON.parse(cachedSession)
            if (parsed?.user) {
              console.log('[AuthContext] Tab hidden - setting loading=false (cache exists)')
              setLoading(false)
            } else {
              console.log('[AuthContext] Tab hidden - cache exists but no user in cache')
            }
          } else {
            console.log('[AuthContext] Tab hidden - no cache found')
          }
        } catch (e) {
          console.error('[AuthContext] Tab hidden - cache check error:', e)
        }
      }
    } else {
      // Tab gained focus - check current state
      const cachedSession = typeof window !== 'undefined' ? localStorage.getItem(SESSION_CACHE_KEY) : null
      const hasCache = !!cachedSession
      
      // Use a function to get current loading state
      setLoading((currentLoading) => {
        console.log(`[AuthContext] Tab gained focus - hasCache=${hasCache}, currentLoading=${currentLoading}, user=${!!user}, userRole=${userRole}`)
        
        // When tab becomes visible again - do nothing
        // Cache is already loaded, no need to restore
        if (hasCache && currentLoading) {
          console.warn('[AuthContext] Tab gained focus - WARNING: hasCache=true but loading=true! Setting loading=false')
          return false
        }
        return currentLoading
      })
    }
  }

    if (typeof window !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange)
    }

    return () => {
      subscription.unsubscribe()
      if (typeof window !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty deps - only run once on mount

  const signIn = async (email: string, password: string) => {
    try {
      // Check if Supabase is properly configured
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
      const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
      const isSupabaseConfigured = supabaseUrl && supabaseAnonKey && !supabaseUrl.includes('placeholder')

      // Test accounts for demo mode - ONLY when Supabase is NOT configured
      const testAccounts: Record<string, { password: string; role: UserRole; name: string; superadmin?: boolean }> = {
        'admin@test.pl': { password: 'test123456', role: 'admin', name: 'Jan Admin', superadmin: true },
        'pracownik@test.pl': { password: 'test123456', role: 'pracownik', name: 'Anna Pracownik' },
        'klient@test.pl': { password: 'test123456', role: 'klient', name: 'Piotr Klient' },
      }

      // Only use test accounts when Supabase is NOT configured (local dev without Supabase)
      if (!isSupabaseConfigured) {
        const account = testAccounts[email]
        if (account && account.password === password) {
          // Create mock session for demo/test accounts
          const mockUser: User = {
            id: `demo-${email.replace('@', '-').replace('.', '-')}`,
            email: email,
            email_confirmed_at: new Date().toISOString(),
            phone: '',
            confirmed_at: new Date().toISOString(),
            last_sign_in_at: new Date().toISOString(),
            app_metadata: {},
            user_metadata: { full_name: account.name },
            identities: [],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            is_anonymous: false,
          }

          const mockSession: Session = {
            access_token: 'demo-token',
            token_type: 'bearer',
            expires_in: 3600,
            expires_at: Math.floor(Date.now() / 1000) + 3600,
            refresh_token: 'demo-refresh-token',
            user: mockUser,
          }

          // Update state - IMPORTANT: pass role to updateSessionCache
          setUserRole(account.role)
          userRoleRef.current = account.role
          setIsSuperadmin(!!account.superadmin)
          updateSessionCache(mockSession, account.role, !!account.superadmin)
          setSession(mockSession)
          setUser(mockUser)
          setLoading(false)

          console.log('[Auth] Demo mode: Logged in as', email, 'with role', account.role)
          return { error: null }
        }

        // Supabase not configured and not a test account
        console.error('[Auth] Supabase configuration error')
        return {
          error: {
            message: 'Wystąpił błąd podczas logowania. Spróbuj ponownie później.',
          },
        }
      }

      // Supabase is configured - always use real authentication

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      // Log error details in production
      if (error && process.env.NODE_ENV === 'production') {
        console.error('[Auth] Sign in error:', {
          message: error.message,
          status: error.status,
        })
      }

      // If login successful, update state immediately (don't wait for onAuthStateChange)
      if (!error && data?.session) {
        // Clear demo subscription when logging in with real account
        try {
          localStorage.removeItem('demo.subscription')
        } catch (e) {
          // Ignore localStorage errors
        }
        
        updateSessionCache(data.session)
        setSession(data.session)
        setUser(data.session.user ?? null)

        // Fetch user role in background
        if (data.session.user) {
          const roleAbortController = new AbortController()
          fetchRoleAbortControllerRef.current = roleAbortController
          fetchUserRole(data.session.user.id, roleAbortController.signal).catch((err) => {
            console.error('[AuthContext] Error fetching user role after login:', err)
          })
        }

        setLoading(false)
      } else if (error) {
        setLoading(false)
      }

      return { error }
    } catch (error: any) {
      console.error('[Auth] Sign in exception:', error)

      if (error?.message?.includes('fetch') || error?.message?.includes('Failed to fetch')) {
        return {
          error: {
            message: 'Nie można połączyć się z serwerem. Sprawdź połączenie internetowe i spróbuj ponownie.',
          },
        }
      }
      return { error: { message: error?.message || 'Wystąpił nieoczekiwany błąd podczas logowania' } }
    }
  }

  const signUp = async (email: string, password: string, fullName?: string) => {
    // Disable auto-confirm and email redirect to use custom Resend emails
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: null, // Disable automatic email redirect
        data: {
          full_name: fullName,
        },
      },
    })
    
    // If signup successful, send confirmation email via Resend
    if (!error && data?.user) {
      try {
        // Try to send confirmation email via Resend
        const response = await fetch('/api/auth/send-confirmation-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            userId: data.user.id,
            fullName,
          }),
        })

        const emailData = await response.json()

        if (!response.ok && !emailData.fallback) {
          // If Resend is configured but failed, log error but don't fail signup
          console.error('[AuthContext] Failed to send confirmation email via Resend:', emailData.error)
        } else if (emailData.fallback) {
          // Resend not configured, Supabase will send default email
          console.warn('[AuthContext] Resend not configured, Supabase will send default confirmation email')
        }
      } catch (emailError) {
        // If API call fails, log but don't fail signup
        // Supabase will still send default email if configured
        console.error('[AuthContext] Error calling send-confirmation-email API:', emailError)
      }
    }
    
    // Don't set session immediately if email is not confirmed
    // User needs to verify email first
    // If session is available (auto-confirm enabled in Supabase), update state
    if (!error && data?.session && data.session.user?.email_confirmed_at) {
      updateSessionCache(data.session)
      setSession(data.session)
      setUser(data.session.user ?? null)
      if (data.session.user) {
        const roleAbortController = new AbortController()
        fetchRoleAbortControllerRef.current = roleAbortController
        fetchUserRole(data.session.user.id, roleAbortController.signal).catch((err) => {
          console.error('[AuthContext] Error fetching user role after signup:', err)
        })
      }
    }
    
    return { data, error }
  }

  const signOut = async () => {
    try {
      await supabase.auth.signOut()
      updateSessionCache(null, null, false)
      setUser(null)
      setUserRole(null)
      setIsSuperadmin(false)
      setSession(null)
      setLoading(false)
      setIsImpersonating(false)
      setImpersonatedFromEmail(null)
      if (typeof window !== 'undefined') {
        localStorage.removeItem(SUPERADMIN_SESSION_CACHE_KEY)
        localStorage.removeItem(IMPERSONATION_ACTIVE_KEY)
        localStorage.removeItem(IMPERSONATION_FROM_EMAIL_KEY)
        localStorage.removeItem('demo.subscription') // Clear demo subscription on logout
      }
      // Redirect to login page
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      }
    } catch (error) {
      console.error('[AuthContext] Sign out error:', error)
      // Even on error, clear state and redirect
      updateSessionCache(null, null, false)
      setUser(null)
      setUserRole(null)
      setIsSuperadmin(false)
      setSession(null)
      setLoading(false)
      setIsImpersonating(false)
      setImpersonatedFromEmail(null)
      if (typeof window !== 'undefined') {
        localStorage.removeItem(SUPERADMIN_SESSION_CACHE_KEY)
        localStorage.removeItem(IMPERSONATION_ACTIVE_KEY)
        localStorage.removeItem(IMPERSONATION_FROM_EMAIL_KEY)
        localStorage.removeItem('demo.subscription') // Clear demo subscription on logout
      }
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      }
    }
  }

  const updateUserRole = async (role: UserRole) => {
    if (!user) return

    const { error } = await supabase
      .from('users')
      // @ts-ignore
      .update({ role })
      .eq('id', user.id)

    if (!error) {
      setUserRole(role)
      userRoleRef.current = role
      try {
        localStorage.setItem(USER_ROLE_CACHE_KEY, role)
      } catch (e) {
        // Ignore cache errors
      }
    }
  }

  const startImpersonation = async (actionLink: string) => {
    if (!session) {
      return { error: 'Brak aktywnej sesji do zapisania' }
    }

    try {
      const payload = {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        user: session.user,
      }
      localStorage.setItem(SUPERADMIN_SESSION_CACHE_KEY, JSON.stringify(payload))
      localStorage.setItem(IMPERSONATION_ACTIVE_KEY, 'true')
      localStorage.setItem(IMPERSONATION_FROM_EMAIL_KEY, session.user?.email || '')
      setIsImpersonating(true)
      setImpersonatedFromEmail(session.user?.email || null)
      window.location.href = actionLink
      return { error: null }
    } catch (error: any) {
      return { error: error?.message || 'Nie udało się rozpocząć impersonacji' }
    }
  }

  const stopImpersonation = async () => {
    try {
      const cached = localStorage.getItem(SUPERADMIN_SESSION_CACHE_KEY)
      if (!cached) {
        setIsImpersonating(false)
        setImpersonatedFromEmail(null)
        return { error: 'Brak zapisanej sesji superadmina' }
      }
      const parsed = JSON.parse(cached)
      await supabase.auth.setSession({
        access_token: parsed.access_token,
        refresh_token: parsed.refresh_token,
      })

      localStorage.removeItem(SUPERADMIN_SESSION_CACHE_KEY)
      localStorage.removeItem(IMPERSONATION_ACTIVE_KEY)
      localStorage.removeItem(IMPERSONATION_FROM_EMAIL_KEY)
      setIsImpersonating(false)
      setImpersonatedFromEmail(null)
      return { error: null }
    } catch (error: any) {
      return { error: error?.message || 'Nie udało się zakończyć impersonacji' }
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        userRole,
        isSuperadmin,
        isImpersonating,
        impersonatedFromEmail,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        updateUserRole,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
